/**
 * RouteInfoBadge
 * ---------------
 * Computes driving distance & duration between two GPS points using Google
 * Directions and renders a compact badge. Emits `onRoute({distance_km, duration_min})`
 * so the parent can display extra info (e.g. price hints).
 * Must be rendered inside an <APIProvider>.
 */
import { useEffect, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Route, Loader2 } from "lucide-react";

export default function RouteInfoBadge({ pickup, dropoff, onRoute, hint }) {
  const routesLib = useMapsLibrary("routes");
  const [state, setState] = useState({ loading: false, km: null, min: null, error: null });

  useEffect(() => {
    if (!routesLib || !pickup?.lat || !dropoff?.lat) {
      setState((s) => ({ ...s, km: null, min: null, error: null }));
      return;
    }
    let cancelled = false;
    setState({ loading: true, km: null, min: null, error: null });
    const svc = new routesLib.DirectionsService();
    svc.route(
      {
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: dropoff.lat, lng: dropoff.lng },
        travelMode: routesLib.TravelMode.DRIVING,
      },
      (result, status) => {
        if (cancelled) return;
        if (status !== "OK" || !result?.routes?.[0]?.legs?.[0]) {
          setState({ loading: false, km: null, min: null, error: "Trajet introuvable" });
          return;
        }
        const leg = result.routes[0].legs[0];
        const km = leg.distance.value / 1000;
        const min = leg.duration.value / 60;
        setState({ loading: false, km, min, error: null });
        onRoute?.({ distance_km: km, duration_min: min });
      }
    );
    return () => { cancelled = true; };
  }, [routesLib, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, onRoute]);

  if (!pickup?.lat || !dropoff?.lat) return null;
  return (
    <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-[#0A2540] text-white px-3 py-2" data-testid="route-info-badge">
      <div className="flex items-center gap-2 min-w-0">
        <Route className="w-4 h-4 text-[#FACC15] shrink-0" />
        {state.loading ? (
          <span className="text-xs inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Calcul du trajet…</span>
        ) : state.error ? (
          <span className="text-xs text-white/60">{state.error}</span>
        ) : (
          <span className="text-xs font-semibold" data-testid="route-info-summary">
            ~{state.km?.toFixed(1)} km · {Math.round(state.min || 0)} min
          </span>
        )}
      </div>
      {hint && !state.loading && !state.error && (
        <span className="text-[10px] text-[#FACC15] font-bold text-right whitespace-nowrap" data-testid="route-info-hint">{hint}</span>
      )}
    </div>
  );
}
